/**
 * Pastille « ? » d'aide contextuelle (migration 0300) : posée devant n'importe
 * quel élément de l'app (`<HelpTip topic="hora" />`), elle ouvre l'explication
 * administrée dans la FAQ (`faq_items.context_key`) au moment où l'élève en a
 * besoin. Rien n'est affiché tant que l'aide n'existe pas (ou est dépubliée) :
 * une clé posée dans le code avant d'être rédigée ne montre jamais de « ? » vide.
 *
 * La modale est rendue dans un PORTAIL (`document.body`) : posée dans un bloc
 * animé (`transform`), un `position: fixed` resterait enfermé dans ce bloc et
 * hériterait de ses styles (majuscules, couleur…).
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { MarkdownMath, cn } from '@jeraisonne/ui';
import { useHelpTopic } from '@/lib/help-faq';

export function HelpTip({
  topic,
  label,
  superscript = false,
  className,
}: {
  /** Clé de l'aide (`faq_items.context_key`). */
  topic: string;
  /** Ce qu'explique la pastille, pour le lecteur d'écran (« HORA »). */
  label?: string;
  /** Pastille en exposant, collée APRÈS le mot (« HORA ? »). */
  superscript?: boolean;
  className?: string;
}) {
  const help = useHelpTopic(topic);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!help) return null;

  return (
    <>
      <button
        type="button"
        aria-label={`Aide : ${label ?? help.question}`}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        // Pastille de 18 px, zone de tap de 44 px (marges négatives : la ligne ne grandit
        // pas). Marge gauche réduite : toujours un petit espace (4 px) entre le texte et la bulle.
        className={cn(
          'group -my-[13px] -ml-[9px] -mr-[13px] inline-flex h-11 w-11 shrink-0 items-center justify-center align-middle',
          superscript && 'relative -top-2 -ml-[11px]',
          className,
        )}
      >
        <span
          aria-hidden
          className={cn(
            'bg-primary-600 ocean:bg-primary-400 ocean:text-ink-50 flex items-center justify-center rounded-full text-xs font-bold leading-none text-white transition-transform group-active:scale-90',
            superscript ? 'h-4 w-4' : 'h-[18px] w-[18px]',
          )}
        >
          ?
        </span>
      </button>
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={help.question}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <div
              className="ocean:bg-[#061433] ocean:border ocean:border-white/10 max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5 text-left normal-case tracking-normal shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-ink-900 text-lg font-bold leading-snug">
                  {help.question}
                </h2>
                <button
                  type="button"
                  autoFocus
                  aria-label="Fermer l’aide"
                  onClick={() => setOpen(false)}
                  className="text-ink-500 hover:bg-ink-100 -mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
              <MarkdownMath className="text-ink-700 mt-3 text-base leading-relaxed [&_p+p]:mt-3">
                {help.answer_md}
              </MarkdownMath>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="bg-primary-600 mt-5 min-h-[2.75rem] w-full rounded-xl text-sm font-semibold text-white active:scale-[0.98]"
              >
                J’ai compris
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
